import React from 'react';
import type { TextProps, ViewProps } from 'react-native';
import { View, StyleSheet } from 'react-native';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import type {
  BottomTabBarProps,
  BottomTabNavigationOptions,
} from '@react-navigation/bottom-tabs';
import { MainTabBarItem } from './MainTabBarItem';
import { theme } from '@garnish/constants';

type MainTabBarProps = {
  readonly hiddenTabs?: readonly string[];
  readonly tabsDisplayingBadge?: readonly string[];
  readonly maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
} & BottomTabBarProps &
  ViewProps;

export const MainTabBar = ({
  state,
  descriptors,
  navigation,
  insets,
  hiddenTabs,
  tabsDisplayingBadge,
  maxFontSizeMultiplier,
  ...viewProps
}: MainTabBarProps) => {
  return (
    <MainTabBarContent {...viewProps} insets={insets}>
      {state.routes
        .filter((route) => !hiddenTabs?.includes(route.name))
        .map((route, index) => {
          const { key, name } = route;
          const { options } = descriptors[key];
          const isActive = state.index === index;
          const hasBadge = Boolean(tabsDisplayingBadge?.includes(name));

          return (
            <Item
              key={key}
              screenKey={key}
              name={name}
              options={options}
              isActive={isActive}
              hasBadge={hasBadge}
              navigation={navigation}
              maxFontSizeMultiplier={maxFontSizeMultiplier}
            />
          );
        })}
    </MainTabBarContent>
  );
};

type ItemProps = {
  readonly screenKey: string;
  readonly name: string;
  readonly options: ItemOptions & BottomTabNavigationOptions;
  readonly isActive: boolean;
  readonly hasBadge: boolean;
  readonly navigation: BottomTabBarProps['navigation'];
  readonly maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
};

type ItemOptions = {
  /**
   * Optional callback used to override the default navigation behavior for this tab.
   */
  readonly customNavigate?: () => void;
};

const Item = ({
  screenKey,
  name,
  options,
  isActive,
  hasBadge,
  navigation,
  maxFontSizeMultiplier,
}: ItemProps) => {
  const { tabBarTestID, title, tabBarAccessibilityLabel, customNavigate } =
    options;

  const itemTitle = title ?? name ?? '';

  const handlePress = React.useCallback(() => {
    const event = navigation.emit({
      type: 'tabPress',
      target: screenKey,
      canPreventDefault: true,
    });

    if (isActive) return;

    if (event.defaultPrevented) return;

    // Optional navigation override for this tab.
    if (customNavigate) {
      customNavigate();

      return;
    }

    // The `merge: true` option makes sure that the params inside the tab screen are preserved
    navigation.navigate({ key: screenKey, merge: true });
  }, [navigation, screenKey, isActive, customNavigate]);

  const handleLongPress = React.useCallback(() => {
    navigation.emit({ type: 'tabLongPress', target: screenKey });
  }, [navigation, screenKey]);

  return (
    <MainTabBarItem
      testID={tabBarTestID}
      title={itemTitle}
      accessibilityLabel={tabBarAccessibilityLabel}
      isActive={isActive}
      hasBadge={hasBadge}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      onPress={handlePress}
      onLongPress={handleLongPress}
    />
  );
};

type MainTabBarContentProps = {
  readonly insets: EdgeInsets;
  readonly children?: React.ReactNode;
  readonly testID?: string;
};

export const MainTabBarContent = ({
  children,
  insets,
  testID,
}: MainTabBarContentProps) => {
  const insetStyle = useStyle(
    () => ({
      paddingLeft: insets.left + theme.spacing['2'],
      paddingRight: insets.right + theme.spacing['2'],
      paddingTop: 0,
      paddingBottom: Math.max(insets.bottom, 10),
    }),
    [insets],
  );

  return (
    <View testID={testID} style={styles.container}>
      <View style={styles.border} />
      <View
        style={[styles.itemsContainer, insetStyle]}
        accessibilityRole="tablist"
      >
        {children}
      </View>
    </View>
  );
};

// eslint-disable-next-line functional/immutable-data
MainTabBar.Content = MainTabBarContent;

// eslint-disable-next-line functional/immutable-data
MainTabBar.Item = MainTabBarItem;

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.KHAKI_3,
  },
  border: {
    backgroundColor: theme.colors.OPACITY.GREEN_1.DARK,
    height: 1,
  },
  itemsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
});
